import { SmartTableColumn } from '@toggle/design-system';
import { CompanyCountryExposureItem } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';

import { ErrorMessageWrapper } from '~/components/error-message-wrapper/ErrorMessageWrapper';
import { FlagIcon } from '~/components/flag-icon/FlagIcon';
import { SectionBase } from '~/modules/overview-knowledge-graph/components/shared/section-base/SectionBase';
import { getKnowledgeGraphSearchParams } from '~/modules/overview-knowledge-graph/utils/knowledge-graph-utils';
import { useCompanyCountryExposure } from '~/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import { appPaths } from '~/routes/app-paths';
import { useGeographicDrawer } from '~/widgets/knowledge-graph-widget/components/geographic-exposure-tab/drawers/useGeographicDrawer';
import { KnowledgeGraphTabType } from '~/widgets/knowledge-graph-widget/KnowledgeGraphWidget';

import styles from './RevampGeographicExposureSection.module.scss';

export interface RevampGeographicExposureSectionProps {
  entityTag: string;
  isInDrawer?: boolean;
}

export const RevampGeographicExposureSection = ({
  entityTag,
  isInDrawer = false,
}: RevampGeographicExposureSectionProps) => {
  const { t } = useTranslation(['widget', 'countries']);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { openCountry } = useGeographicDrawer();

  const {
    data = [],
    isError,
    isFetching,
  } = useCompanyCountryExposure({
    entityTag,
  });

  const knowledgeGraphSearchParams = getKnowledgeGraphSearchParams({
    entityTag,
    searchParams,
    subtab: KnowledgeGraphTabType.GeographicExposure,
  }).toString();

  const linkToKnowledgeGraph = `${appPaths.assetOverview}?${knowledgeGraphSearchParams}`;

  const tableColumns: SmartTableColumn<CompanyCountryExposureItem>[] = [
    {
      label: t('widget:assetOverview.knowledgeGraphRevamp.columns.country'),
      key: 'code',
      render: value => (
        <div className={styles.root}>
          <FlagIcon countryCode={value} customSize={28} variant="circular" />
          <div className={styles.title}>{t(`countries:${value}`)}</div>
        </div>
      ),
    },
  ];

  if (isError) {
    return <ErrorMessageWrapper displayIcon />;
  }

  return (
    <SectionBase
      title={t('widget:assetOverview.knowledgeGraphRevamp.geographicExposure')}
      tableData={data.slice(0, 3).map(d => ({ ...d, key: d.code }))}
      tableColumns={tableColumns}
      isFetching={isFetching}
      onExpand={() => navigate(linkToKnowledgeGraph)}
      onRowClick={item =>
        isInDrawer ? navigate(linkToKnowledgeGraph) : openCountry(item.code)
      }
    />
  );
};
